"use client";
import {useState, useEffect, useRef} from "react";
import {m, Variants} from "framer-motion";
import Image from "next/image";
import {FiCode, FiServer, FiActivity, FiCpu, FiClock} from "react-icons/fi";
import AnimatedSection from "@/components/ui/AnimatedSection";
import {type SectionProps} from "@/types/types";

interface ContributionDay {
  contributionCount: number;
  date: string;
  color: string;
}

interface ContributionWeek {
  contributionDays: ContributionDay[];
}

interface ContributionCalendar {
  totalContributions: number;
  weeks: ContributionWeek[];
}

const stats = [
  {icon: FiCode, value: "1+", label: "YEARS_CODING"},
  {icon: FiServer, value: "10+", label: "PROJECTS_BUILT"},
  {icon: FiActivity, value: "SYSTEM", label: "STATUS_NOMINAL"},
];

const techBadges = [
  "React", "Next.js", "TypeScript", "TailwindCSS",
  "Node.js", "FramerMotion", "MongoDB", "Git",
];

export default function AboutSection({delay = 0}: SectionProps) {
  // Clock state
  const [timeStr, setTimeStr] = useState("00:00:00");
  
  // Contributions state
  const [calendar, setCalendar] = useState<ContributionCalendar | null>(null);
  const [loading, setLoading] = useState(true);

  // Responsive Grid State
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Nigeria Clock
  useEffect(() => {
    const updateClock = () => {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "Africa/Lagos",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      };
      const formatter = new Intl.DateTimeFormat("en-US", options);
      setTimeStr(formatter.format(new Date()));
    };

    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Contributions
  useEffect(() => {
    fetch("/api/github/contributions")
      .then((res) => res.json())
      .then((data) => {
        if (data && !data.error) {
          setCalendar(data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const renderContributionsGrid = () => {
    // Each column is w-2.5 (10px) + gap-1.5 (6px) = 16px total.
    // If we have a width, calculate how many weeks fit. Otherwise default to 18.
    const totalWeeks = containerWidth > 0 ? Math.floor(containerWidth / 16) : 18;
    const daysPerWeek = 7;

    const containerVariants = {
      hidden: {opacity: 0},
      show: {
        opacity: 1,
        transition: {staggerChildren: 0.01}, // Very fast stagger
      },
    };

    const itemVariants: Variants = {
      hidden: {opacity: 0, scale: 0.5},
      show: {opacity: 1, scale: 1, transition: {type: "spring", stiffness: 300, damping: 20}},
    };

    if (calendar && calendar.weeks) {
      // Use real data, display calculated number of weeks
      const weeks = calendar.weeks.slice(-totalWeeks);
      return (
        <m.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex gap-1.5 overflow-hidden py-2"
        >
          {weeks.map((week: ContributionWeek, wIdx: number) => (
            <div key={`w-${wIdx}`} className="flex flex-col gap-1.5 shrink-0">
              {week.contributionDays.map((day: ContributionDay, dIdx: number) => {
                const count = day.contributionCount;
                let bgClass = "bg-zinc-950 border-zinc-800";
                if (count > 0 && count <= 2) bgClass = "bg-yellow-950 border-yellow-950";
                else if (count > 2 && count <= 5) bgClass = "bg-yellow-600 border-yellow-600";
                else if (count > 5) bgClass = "bg-yellow-400 border-yellow-400";
                return (
                  <m.div
                    key={`d-${dIdx}`}
                    variants={itemVariants}
                    className={`w-2.5 h-2.5 border ${bgClass} hover:border-yellow-200 transition-colors cursor-crosshair`}
                    title={`${count} commits on ${day.date}`}
                  />
                );
              })}
            </div>
          ))}
        </m.div>
      );
    }

    // Offline / Demo Fallback Mode
    const seed = 12345;
    const pseudoRandom = (w: number, d: number) => {
      const val = Math.sin(w * 5 + d * 17 + seed) * 10000;
      return Math.floor((val - Math.floor(val)) * 10);
    };

    return (
      <div className="relative">
        <m.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex gap-1.5 overflow-hidden py-2 opacity-40 blur-[0.5px]"
        >
          {Array.from({length: totalWeeks}).map((_, wIdx) => (
            <div key={`w-demo-${wIdx}`} className="flex flex-col gap-1.5 shrink-0">
              {Array.from({length: daysPerWeek}).map((_, dIdx) => {
                const rand = pseudoRandom(wIdx, dIdx);
                let bgClass = "bg-zinc-950";
                if (rand > 4 && rand <= 7) bgClass = "bg-yellow-950";
                else if (rand > 7 && rand <= 9) bgClass = "bg-yellow-700";
                else if (rand > 9) bgClass = "bg-yellow-400";
                return (
                  <m.div
                    key={`d-demo-${dIdx}`}
                    variants={itemVariants}
                    className={`w-2.5 h-2.5 border border-zinc-900 ${bgClass}`}
                  />
                );
              })}
            </div>
          ))}
        </m.div>
        <div className="absolute inset-0 flex flex-col items-center justify-center font-mono text-[10px] text-yellow-500 font-bold bg-zinc-950/20 select-none pointer-events-none">
          <span>&gt;&gt;_OFFLINE_SIMULATION_MODE</span>
          <span className="text-zinc-500 text-[8px] mt-1">NO_TOKEN_FOUND</span>
        </div>
      </div>
    );
  };

  return (
    <AnimatedSection
      delay={delay}
      id="about"
      className="py-24 border-t-2 border-zinc-800"
      aria-label="About Joel Olajire"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14">
          <h2 className="text-4xl md:text-5xl font-black uppercase text-zinc-50 tracking-tighter">
            BIOS_INFO
          </h2>
          <div className="w-16 h-1.5 bg-yellow-400 mt-3" />
        </div>

        {/* Bento Grid */}
        <div className="grid gap-4 md:grid-cols-6 auto-rows-auto font-mono">
          {/* Block 1: Bio Narrative (Col Span 4) */}
          <div className="md:col-span-4 border-2 border-zinc-800 bg-zinc-900/40 p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] text-zinc-500 mb-4 uppercase tracking-widest">
                [SECTION: PROFILE_RECORDS]
              </div>
              <h3 className="text-xl font-bold uppercase text-zinc-50 mb-4 tracking-tighter border-b border-zinc-800 pb-2">
                Joel Olajire // Frontend Engineer
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                Based in <span className="text-yellow-400">Ibafo, Nigeria</span>. 
                Specializing in building high-performance, accessible, and utility-driven web applications.
                Architecting clean structures, strict nominal typing pipelines, and highly responsive user dashboards.
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Focused on micro-interactions, layout composition, and writing pristine code that complies with absolute type safety.
              </p>
            </div>
            <div className="text-[10px] text-zinc-600 mt-6 select-none">
              STATUS: ONLINE // SECURITY_LEVEL: ROOT
            </div>
          </div>

          {/* Block 2: Image block (Col Span 2) */}
          <div className="md:col-span-2 border-2 border-zinc-800 bg-zinc-950 p-4 flex flex-col items-center justify-center relative group">
            <div className="w-full aspect-square relative border border-zinc-800 overflow-hidden">
              <Image
                src="/founder-pics.jpg"
                alt="Joel Olajire"
                fill
                priority
                className="object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute inset-0 bg-yellow-400/5 group-hover:bg-transparent transition-all duration-300" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-3 uppercase tracking-wider text-center w-full">
              ID_TAG: JOEL_OLAJIRE.IMG
            </div>
          </div>

          {/* Block 3: GitHub Contributions Grid (Col Span 4) */}
          <div className="md:col-span-4 border-2 border-zinc-800 bg-zinc-900/40 p-6" ref={containerRef}>
            <div className="flex items-center justify-between mb-4 border-b border-zinc-800 pb-2">
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">[WIDGET: DEV_ACTIVITY_GRID]</span>
              <span className="text-xs text-yellow-400 font-bold">
                {loading ? "FETCHING..." : calendar ? `${calendar.totalContributions || 0} COMMITS/YEAR` : "OFFLINE"}
              </span>
            </div>
            
            {loading ? (
              <div className="h-24 flex items-center justify-center text-xs text-zinc-600">
                &gt;&gt; ESTABLISHING_SECURE_CONNECTION...
              </div>
            ) : (
              renderContributionsGrid()
            )}
          </div>

          {/* Block 4: Time Zone Clock (Col Span 2) */}
          <div className="md:col-span-2 border-2 border-zinc-800 bg-zinc-900/40 p-6 flex flex-col justify-between">
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-4">[MODULE: CLOCK_FEED]</span>
              <div className="flex items-center gap-2 mb-2 text-zinc-400">
                <FiClock size={14} className="text-yellow-400" />
                <span className="text-xs text-zinc-500">IBAFO, NIGERIA (UTC+1)</span>
              </div>
              <div className="text-3xl font-black text-yellow-400 font-mono tracking-widest py-1 border-y border-zinc-800 my-3">
                {timeStr}
              </div>
            </div>
            <div className="text-[9px] text-zinc-600 leading-tight">
              LOAD: NORMAL // SYNC_STATUS: OK
            </div>
          </div>

          {/* Block 5: Tech stack loaded (Col Span 3) */}
          <div className="md:col-span-3 border-2 border-zinc-800 bg-zinc-900/40 p-6">
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-4">[SYSTEM: MODULES_LOADED]</span>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {techBadges.map((tech) => (
                <div
                  key={tech}
                  className="px-3 py-2 bg-zinc-950 text-zinc-300 border border-zinc-800 flex items-center gap-2 select-none hover:border-yellow-400 transition-colors"
                >
                  <FiCpu className="text-yellow-400 shrink-0" size={10} />
                  <span className="truncate">{tech}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Block 6: Code Stats (Col Span 3) */}
          <div className="md:col-span-3 border-2 border-zinc-800 bg-zinc-900/40 p-6 flex flex-col justify-between">
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-4">[TELEMETRY: STATS_MONITOR]</span>
            <div className="grid grid-cols-3 gap-3">
              {stats.map(({icon: Icon, value, label}) => (
                <div
                  key={label}
                  className="p-3 bg-zinc-950 border border-zinc-800 text-center"
                >
                  <Icon className="mx-auto mb-2 text-yellow-400" size={16} />
                  <div className="text-lg font-bold text-zinc-50">{value}</div>
                  <div className="text-[9px] text-zinc-500 mt-1 truncate">{label}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-zinc-600 mt-4 select-none">
              GRID: ASYMMETRICAL // FEED: NOMINAL
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}
